/**
 * Notification SSE transport (Phase 2).
 *
 * Maintains a single always-on `EventSource` to the on-box anna-chat service
 * `GET /notifications` stream. AWS-side producers (gmail / routine / reminder /
 * singularity / lupulus / Irene / Aura) POST proactive `notification` frames to
 * the box `/notify` webhook, which fans them out here — replacing the old API
 * Gateway WebSocket push path.
 *
 * `EventSource` cannot set an `Authorization` header, so the browser session
 * token rides the `?token=` query fallback that the service's `authenticate`
 * dependency already accepts.
 *
 * A process-wide singleton with a subscriber set: Chat and the floating widget
 * both register a handler, so one socket serves every open surface. The browser
 * auto-reconnects on transient drops; we add a manual backoff for hard errors.
 */

const SSE_URL =
  import.meta.env.VITE_SSE_URL || "https://anna-chat-dev.pretorianhackers.com";

class NotificationStream {
  constructor() {
    this._token = null;
    this._es = null;
    this._subscribers = new Set();
    this._reconnectTimer = null;
    this._backoffMs = 1000;
  }

  /** Register a frame handler. Returns an unsubscribe function. */
  subscribe(handler) {
    this._subscribers.add(handler);
    return () => this._subscribers.delete(handler);
  }

  /** Connect (idempotent). Reconnects if the token changed. */
  connect(token) {
    if (!token) return;
    if (this._es && this._token === token) return;
    this._token = token;
    this._open();
  }

  _open() {
    this._teardown();
    const url = `${SSE_URL}/notifications?token=${encodeURIComponent(this._token)}`;
    let es;
    try {
      es = new EventSource(url);
    } catch (err) {
      console.error("Notification stream failed to open:", err);
      this._scheduleReconnect();
      return;
    }
    this._es = es;

    es.onopen = () => {
      this._backoffMs = 1000; // reset backoff on a healthy connection
    };

    es.onmessage = (evt) => {
      if (!evt.data) return;
      let data;
      try {
        data = JSON.parse(evt.data);
      } catch {
        return; // heartbeat comments never reach onmessage; ignore stray data
      }
      for (const handler of this._subscribers) {
        try {
          handler(data);
        } catch (err) {
          console.error("Notification handler threw:", err);
        }
      }
    };

    es.onerror = () => {
      // EventSource auto-retries on transient drops; for hard failures the
      // connection is CLOSED — reopen with backoff.
      if (es.readyState === EventSource.CLOSED) {
        this._scheduleReconnect();
      }
    };
  }

  _scheduleReconnect() {
    this._teardown();
    if (this._reconnectTimer) return;
    const delay = this._backoffMs;
    this._backoffMs = Math.min(this._backoffMs * 2, 30000);
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._token) this._open();
    }, delay);
  }

  _teardown() {
    if (this._es) {
      try {
        this._es.close();
      } catch {
        /* already closed */
      }
      this._es = null;
    }
  }

  disconnect() {
    this._token = null;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._teardown();
  }
}

export const notificationStream = new NotificationStream();
